import { useEffect } from "react";
import SEO_DATA from "@/seo/seoPages.json";

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Lightweight SEO/GEO helper for our CRA SPA (no react-helmet dependency).
 * Sets title, description, canonical, Open Graph / Twitter tags and injects JSON-LD.
 * Uses window.location.origin at runtime so it stays correct in preview and production.
 */
export function useSeo({ title, description, path, image, jsonLd } = {}) {
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    // Canonique UNIQUE (www.planpermis.fr) : évite le contenu dupliqué www / sans www / preview.
    const origin = SEO_DATA.site || (typeof window !== "undefined" ? window.location.origin : "");
    const pathname = path || (typeof window !== "undefined" ? window.location.pathname : "");
    const url = origin + pathname;

    if (title) {
      document.title = title;
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "PlanPermis");
    upsertMeta("property", "og:url", url);
    upsertMeta("name", "twitter:card", "summary_large_image");
    if (image) {
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:image", image);
    }
    upsertLink("canonical", url);

    let script;
    if (jsonLdStr) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.text = jsonLdStr;
      document.head.appendChild(script);
    }
    return () => {
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, path, image, jsonLdStr]);
}

export default useSeo;
